import React, { useEffect, useRef } from "react";
import moment from "moment";
import { useLayout } from '../../../context/LayoutContext';
import '../../../assets/scss/components/wssp/ChatMessages.scss';
import { useWhatsApp } from "../../../context/WhatsAppContext";

const ChatMessages = () => {
  const { theme } = useLayout();
  const { 
    selectedChat, 
    messages, 
    qrCode, 
    authenticated, 
    loading, 
    error,
    handleRetryMedia,
    getMediaTypeFromMimetype 
  } = useWhatsApp();
  
  // Referencias para scroll automático optimizado
  const messagesEndRef = useRef(null);
  const messagesScrollRef = useRef(null);

  // Scroll automático a nuevos mensajes con throttling
  useEffect(() => {
    const scrollToBottom = () => {
      if (messagesEndRef.current && messagesScrollRef.current) {
        const scrollContainer = messagesScrollRef.current;
        const isNearBottom = scrollContainer.scrollHeight - scrollContainer.scrollTop - scrollContainer.clientHeight < 100;
        
        // Solo scroll automático si está cerca del final
        if (isNearBottom) {
          messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
      }
    };

    const timeoutId = setTimeout(scrollToBottom, 100);
    return () => clearTimeout(timeoutId);
  }, [messages]);

  // Auto-scroll optimizado en resize
  useEffect(() => {
    const handleResize = () => {
      if (messagesEndRef.current && messagesScrollRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: "auto" });
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Función optimizada para renderizar contenido multimedia
  const renderMessageContent = (message) => {
    if (message.hasMedia) {
      // Estados de carga de multimedia
      if (message.mediaStatus === "needsLoading") {
        return (
          <div className={`GM__${theme}-wssp-media-queued`}>
            <div className={`GM__${theme}-wssp-media-icon`}>⏳</div>
            <span>Contenido multimedia pendiente</span>
            <button
              className={`GM__${theme}-wssp-load-media-button`}
              onClick={() => handleRetryMedia(message.id)}
              aria-label="Cargar contenido multimedia"
            >
              Cargar contenido
            </button>
          </div>
        );
      }

      if (message.mediaStatus === "loading") {
        return (
          <div className={`GM__${theme}-wssp-media-loading`}>
            <div className={`GM__${theme}-wssp-loading-spinner small`}></div>
            <span>Cargando multimedia...</span>
          </div>
        );
      }

      if (message.mediaStatus === "error") {
        return (
          <div className={`GM__${theme}-wssp-media-error`}>
            <div className={`GM__${theme}-wssp-error-icon small`}>⚠️</div>
            <span>Error al cargar: {message.mediaError || "Desconocido"}</span>
            <button
              className={`GM__${theme}-wssp-retry-media-button`}
              onClick={() => handleRetryMedia(message.id)}
              aria-label="Reintentar carga de multimedia"
            >
              Reintentar
            </button>
          </div>
        );
      }

      // Renderizar multimedia según tipo
      if (message.mediaData) {
        const { mimetype, data, filename, type } = message.mediaData;
        const mediaType = type || getMediaTypeFromMimetype(mimetype);

        if (!data || !mimetype) {
          return (
            <div className={`GM__${theme}-wssp-media-invalid`}>
              <span>Datos de multimedia incompletos</span>
              <button
                className={`GM__${theme}-wssp-retry-media-button`}
                onClick={() => handleRetryMedia(message.id)}
                aria-label="Reintentar carga de multimedia"
              >
                Reintentar
              </button>
            </div>
          );
        }

        // Stickers
        if (mediaType === "sticker" || mimetype === "image/webp") {
          return (
            <div className={`GM__${theme}-wssp-sticker-container`}>
              <img
                src={`data:${mimetype};base64,${data}`}
                alt="Sticker"
                className={`GM__${theme}-wssp-media-sticker`}
                loading="lazy"
              />
            </div>
          );
        }

        // Imágenes
        if (mediaType === "image" || mimetype?.startsWith("image/")) {
          return (
            <div className={`GM__${theme}-wssp-image-container`}>
              <img
                src={`data:${mimetype};base64,${data}`}
                alt="Imagen"
                className={`GM__${theme}-wssp-media-image`}
                loading="lazy"
                onClick={(e) => e.target.classList.toggle("expanded")}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.target.classList.toggle("expanded");
                  }
                }}
              />
            </div>
          );
        }

        // Videos
        if (mediaType === "video" || mimetype?.startsWith("video/")) {
          return (
            <div className={`GM__${theme}-wssp-video-container`}>
              <video 
                controls 
                className={`GM__${theme}-wssp-media-video`} 
                preload="metadata"
                controlsList="nodownload"
              >
                <source src={`data:${mimetype};base64,${data}`} type={mimetype} />
                Tu navegador no soporta la reproducción de videos.
              </video>
            </div>
          );
        }

        // Audio
        if (mediaType === "audio" || mimetype?.startsWith("audio/")) {
          return (
            <div className={`GM__${theme}-wssp-audio-container`}>
              <div className={`GM__${theme}-wssp-audio-icon`}>🎵</div>
              <audio controls className={`GM__${theme}-wssp-media-audio`}>
                <source src={`data:${mimetype};base64,${data}`} type={mimetype} />
                Tu navegador no soporta la reproducción de audio.
              </audio>
            </div>
          );
        }

        // Documentos
        if (mediaType === "document" || (!mimetype?.startsWith("image/") && !mimetype?.startsWith("video/") && !mimetype?.startsWith("audio/"))) {
          let icon = "📄";
          let typeLabel = "Documento";

          // Detección de tipo de archivo optimizada
          const mimeTypeMap = {
            "application/pdf": { icon: "📑", typeLabel: "PDF" },
            "application/vnd.ms-excel": { icon: "📊", typeLabel: "Excel" },
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": { icon: "📊", typeLabel: "Excel" },
            "application/msword": { icon: "📝", typeLabel: "Word" },
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document": { icon: "📝", typeLabel: "Word" },
            "application/vnd.ms-powerpoint": { icon: "📽️", typeLabel: "PowerPoint" },
            "application/vnd.openxmlformats-officedocument.presentationml.presentation": { icon: "📽️", typeLabel: "PowerPoint" }
          };

          const fileType = mimeTypeMap[mimetype];
          if (fileType) {
            icon = fileType.icon;
            typeLabel = fileType.typeLabel;
          }

          return (
            <div className={`GM__${theme}-wssp-document-container`}>
              <div className={`GM__${theme}-wssp-document-icon`}>{icon}</div>
              <div className={`GM__${theme}-wssp-document-info`}>
                <span className={`GM__${theme}-wssp-document-name`}>
                  {filename || "Documento"}
                </span>
                <span className={`GM__${theme}-wssp-document-type`}>
                  {typeLabel}
                </span>
              </div>
              <a 
                href={`data:${mimetype};base64,${data}`}
                download={filename || "documento"}
                className={`GM__${theme}-wssp-download-button`}
                title="Descargar archivo"
                aria-label={`Descargar ${filename || "documento"}`}
              >
                ⬇️
              </a>
            </div>
          );
        }

        // Tipo genérico
        return (
          <div className={`GM__${theme}-wssp-generic-media`}>
            <div className={`GM__${theme}-wssp-media-icon`}>📎</div>
            <div className={`GM__${theme}-wssp-media-info`}>
              <span>{filename || "Archivo"}</span>
              <span>{mimetype}</span>
            </div>
            <a 
              href={`data:${mimetype};base64,${data}`}
              download={filename || "archivo"}
              className={`GM__${theme}-wssp-download-button`}
              aria-label={`Descargar ${filename || "archivo"}`}
            >
              ⬇️
            </a>
          </div>
        );
      }
    }

    // Mensajes de texto con enlaces optimizado
    if (!message.body) return null;

    return (
      <div className={`GM__${theme}-wssp-message-text`}>
        {message.body.split(/(\s+)/).map((part, i) => {
          // Regex mejorada para detectar URLs
          const urlRegex = /^(https?:\/\/[^\s]+|www\.[^\s]+)$/i;
          
          if (urlRegex.test(part.trim())) {
            const href = part.trim().startsWith("www.") ? `https://${part.trim()}` : part.trim();
            return (
              <a 
                key={i}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className={`GM__${theme}-wssp-message-link`}
              >
                {part.trim()}
              </a>
            );
          }
          return <React.Fragment key={i}>{part}</React.Fragment>;
        })}
      </div>
    );
  };

  // Función optimizada para agrupar mensajes
  const groupMessagesByDate = () => {
    if (!messages || messages.length === 0) return {};
    
    return messages.reduce((groups, message) => {
      const date = moment(message.timestamp).format("YYYY-MM-DD");
      if (!groups[date]) groups[date] = [];
      groups[date].push(message);
      return groups;
    }, {});
  };

  // Formatear fecha con memoización
  const formatDate = (dateStr) => {
    const messageDate = moment(dateStr);
    const today = moment().startOf("day");
    const yesterday = moment().subtract(1, "days").startOf("day");

    if (messageDate.isSame(today, "day")) return "Hoy";
    if (messageDate.isSame(yesterday, "day")) return "Ayer";
    return messageDate.format("DD/MM/YYYY");
  };

  // Obtener icono de estado optimizado
  const getStatusIcon = (status, fromMe) => {
    if (!fromMe) return null;

    const statusMap = {
      PENDING: { icon: "⏱️", title: "Pendiente", class: "pending" },
      SENT: { icon: "✓", title: "Enviado", class: "sent" },
      RECEIVED: { icon: "✓✓", title: "Entregado", class: "received" },
      READ: { icon: "✓✓", title: "Leído", class: "read" }
    };

    const statusInfo = statusMap[status] || { icon: "?", title: "Desconocido", class: "unknown" };

    return (
      <span 
        className={`GM__${theme}-wssp-status-icon ${statusInfo.class}`} 
        title={statusInfo.title}
        aria-label={statusInfo.title}
      >
        {statusInfo.icon}
      </span>
    );
  };

  // Estados especiales - Error
  if (error) {
    return (
      <div className={`GM__${theme}-wssp-messages-container`}>
        <div className={`GM__${theme}-wssp-error-state`}>
          <div className={`GM__${theme}-wssp-error-content`}>
            <div className={`GM__${theme}-wssp-error-icon`}>⚠️</div>
            <h3>Error de conexión</h3>
            <p>{error}</p>
            <button 
              className={`GM__${theme}-wssp-retry-button`}
              onClick={() => window.location.reload()}
              aria-label="Reintentar conexión"
            >
              Reintentar conexión
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Estados especiales - QR Code
  if (!authenticated && qrCode) {
    return (
      <div className={`GM__${theme}-wssp-messages-container`}>
        <div className={`GM__${theme}-wssp-qr-state`}>
          <div className={`GM__${theme}-wssp-qr-content`}>
            <div className={`GM__${theme}-wssp-qr-code`}>
              <h3>Conecta tu WhatsApp</h3>
              <img src={qrCode} alt="WhatsApp QR Code" />
              <p>Escanea este código QR con la cámara de WhatsApp en tu teléfono</p>
              <div className={`GM__${theme}-wssp-qr-instructions`}>
                <ol>
                  <li>Abre WhatsApp en tu teléfono</li>
                  <li>Toca el menú o configuración</li>
                  <li>Selecciona "Dispositivos vinculados"</li>
                  <li>Toca "Vincular un dispositivo"</li>
                  <li>Apunta tu teléfono hacia esta pantalla</li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Estados especiales - Loading
  if (loading) {
    return (
      <div className={`GM__${theme}-wssp-messages-container`}>
        <div className={`GM__${theme}-wssp-loading-state`}>
          <div className={`GM__${theme}-wssp-loading-content`}>
            <div className={`GM__${theme}-wssp-loading-spinner`}></div>
            <p>Cargando mensajes...</p>
          </div>
        </div>
      </div>
    );
  }

  // Estados especiales - Welcome
  if (!selectedChat) {
    return (
      <div className={`GM__${theme}-wssp-messages-container`}>
        <div className={`GM__${theme}-wssp-welcome-state`}>
          <div className={`GM__${theme}-wssp-welcome-content`}>
            <div className={`GM__${theme}-wssp-welcome-icon`}>💬</div>
            <h2>WhatsApp Observer</h2>
            <p>Selecciona una conversación del panel izquierdo para ver los mensajes</p>
            <div className={`GM__${theme}-wssp-features`}>
              <div className={`GM__${theme}-wssp-feature`}>
                <span>📱</span>
                <span>Sincronización en tiempo real</span>
              </div>
              <div className={`GM__${theme}-wssp-feature`}>
                <span>🔍</span>
                <span>Búsqueda avanzada de mensajes</span>
              </div>
              <div className={`GM__${theme}-wssp-feature`}>
                <span>📎</span>
                <span>Soporte para multimedia</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Renderizado principal con área de scroll separada
  const groupedMessages = groupMessagesByDate();

  return (
    <div className={`GM__${theme}-wssp-messages-container`}>
      <div 
        ref={messagesScrollRef}
        className={`GM__${theme}-wssp-messages-scroll`}
      >
        {Object.keys(groupedMessages).length === 0 ? (
          <div className={`GM__${theme}-wssp-no-messages`}>
            <p>No hay mensajes en esta conversación</p>
          </div>
        ) : (
          Object.keys(groupedMessages)
            .sort() // Ordenar fechas cronológicamente
            .map((date) => (
              <div key={date} className={`GM__${theme}-wssp-message-group`}>
                <div className={`GM__${theme}-wssp-date-separator`}>
                  <span>{formatDate(date)}</span>
                </div>

                {groupedMessages[date].map((message) => (
                  <div
                    key={message.id}
                    className={`GM__${theme}-wssp-message ${
                      message.fromMe ? "outgoing" : "incoming"
                    } ${message.id.startsWith("temp_") ? "pending" : ""}`}
                  >
                    <div className={`GM__${theme}-wssp-message-content`}>
                      {selectedChat.isGroup && !message.fromMe && (
                        <span className={`GM__${theme}-wssp-message-sender`}>
                          {message.authorName || "Usuario"}
                        </span>
                      )}

                      {renderMessageContent(message)}

                      <div className={`GM__${theme}-wssp-message-meta`}>
                        <span className={`GM__${theme}-wssp-message-time`}>
                          {moment(message.timestamp).format("HH:mm")}
                        </span>
                        {getStatusIcon(message.status, message.fromMe)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))
        )}
        <div ref={messagesEndRef} />
      </div>
    </div>
  );
};

export default ChatMessages;